import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import DynamicIcons from '../CredentialStore/DynamicIcons';
import { searchPipedreamApps } from '../../utils/PipeDreamUtil';

const Label = styled(Typography)({
  paddingBottom: '8px',
  fontFamily: 'Open Sans !important',
  fontSize: '14px',
  fontWeight: '400 !important',
  lineHeight: '19.07px',
  textAlign: 'left',
});

const OptionItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  '& > *:first-of-type': {
    marginRight: '16px',
  },
});

const LoadMoreButton = styled(Button)({
  width: '100%',
  textTransform: 'none',
  justifyContent: 'center',
  borderRadius: 0,
});

const SelectedIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginLeft: '4px',
});

/**
 * Reusable Pipedream app picker backed by live catalog search. Searches on blur or Enter (never per
 * keystroke) and paginates with the Pipedream cursor via a "Load more" control. The `capability`
 * prop selects which catalog to search ('triggers' today, 'actions' for the future action feature).
 */
const PipedreamAppSearchPicker = ({
  capability = 'triggers',
  value = null,
  onSelect,
  title,
  placeholder = 'Search for an app...',
  disabled = false,
  defaultOptions = [],
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const lastQueryRef = useRef('');

  const runSearch = useCallback(
    async query => {
      const trimmed = (query || '').trim();
      // Empty query: keep showing the curated defaultOptions (connected/popular apps) instead of
      // firing a doomed "list all apps" (q="") request and flipping hasSearched. This is why tabbing
      // away (onBlur) or pressing Enter on an empty box is a no-op.
      if (!trimmed) return;
      setLoading(true);
      setHasSearched(true);
      lastQueryRef.current = trimmed;
      try {
        const { apps, endCursor } = await searchPipedreamApps(trimmed, null, capability);
        setOptions(apps);
        setCursor(endCursor);
        setHasMore(Boolean(endCursor));
      } catch (error) {
        setOptions([]);
        setCursor(null);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [capability]
  );

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const { apps, endCursor } = await searchPipedreamApps(
        lastQueryRef.current,
        cursor,
        capability
      );
      setOptions(prev => [...prev, ...apps]);
      setCursor(endCursor);
      setHasMore(Boolean(endCursor));
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [cursor, capability]);

  const handleInputChange = useCallback((_event, newInput, reason) => {
    // 'input' = user typing; 'reset' = MUI syncing the box to the selected/restored value's label.
    // Honoring 'reset' is what "locks in" the chosen app name instead of leaving the typed text.
    if (reason === 'input' || reason === 'reset') {
      setInputValue(newInput);
    }
  }, []);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        runSearch(inputValue);
      }
    },
    [inputValue, runSearch]
  );

  const PaperWithLoadMore = useCallback(
    paperProps => (
      <Paper {...paperProps}>
        {paperProps.children}
        {hasMore && (
          <LoadMoreButton
            onMouseDown={event => event.preventDefault()}
            onClick={loadMore}
            disabled={loading}
          >
            Load more
          </LoadMoreButton>
        )}
      </Paper>
    ),
    [hasMore, loading, loadMore]
  );

  const noOptionsText = hasSearched
    ? 'No matching apps'
    : 'Type an app name, then press Enter';

  // Before the user searches, surface their already-connected apps (defaultOptions); switch to live
  // search results once a search has run. MUI Autocomplete also warns when the controlled `value` is
  // not present in `options`, so keep the selected app in the list to satisfy the equality check.
  const mergedOptions = useMemo(() => {
    const base = hasSearched ? options : defaultOptions;
    if (value?.nameSlug && !base.some(option => option?.nameSlug === value.nameSlug)) {
      return [value, ...base];
    }
    return base;
  }, [value, options, defaultOptions, hasSearched]);

  return (
    <Box sx={sx}>
      {title && <Label>{title}</Label>}
      <Autocomplete
        value={value}
        onChange={(_event, newValue) => {
          onSelect?.(newValue);
          setInputValue(newValue?.name || newValue?.nameSlug || '');
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={() => runSearch(inputValue)}
        options={mergedOptions}
        filterOptions={appOptions => appOptions}
        getOptionLabel={option => option?.name || option?.nameSlug || ''}
        isOptionEqualToValue={(option, val) => option?.nameSlug === val?.nameSlug}
        loading={loading}
        loadingText="Searching apps..."
        noOptionsText={noOptionsText}
        disabled={disabled}
        fullWidth
        autoHighlight
        PaperComponent={PaperWithLoadMore}
        renderOption={(optionProps, option) => (
          <OptionItem {...optionProps} key={option?.nameSlug}>
            {option?.imgSrc && <DynamicIcons icons={[option.imgSrc]} />}
            {option?.name || option?.nameSlug}
          </OptionItem>
        )}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              startAdornment: value?.imgSrc ? (
                <SelectedIcon>
                  <DynamicIcons icons={[value.imgSrc]} />
                </SelectedIcon>
              ) : (
                params.InputProps.startAdornment
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

PipedreamAppSearchPicker.propTypes = {
  capability: PropTypes.oneOf(['triggers', 'actions']),
  value: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  defaultOptions: PropTypes.array,
  sx: PropTypes.object,
};

export default PipedreamAppSearchPicker;
